<script setup>
import { ref, computed, watch, onMounted, useTemplateRef, getCurrentInstance } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useNotification } from "@kyvg/vue3-notification"
import UserService from '@/services/UserService'
import Spinner from "@/components/ui/Spinner.vue"
import FormInput from '../../components/Atoms/FormInput.vue'
import FormButton from '../../components/Atoms/FormButton.vue'

const instance = getCurrentInstance()
const root = instance.proxy.$root
const { notify } = useNotification()
const store = useStore()

//Refs
const processing = ref(false)
const apiKeyProcessing = ref(false)
const form = ref({
  apiKey: null
})

// Computed 
const canAccessDevelopers = computed(() => store.getters['User/canAccessDevelopers'])
const userEmail = computed(() => store.getters['User/userEmail'])
const userloaded = computed(() => store.getters['User/userLoaded'])

// Hooks
onMounted(async () => {
  // track event
  window.trackEvent('Page Viewed', { page: 'Developer Settings' })
})

// Methods

const clipboardCopy = (text) => {
  try {
    if (text instanceof Function) text = text()
    navigator.clipboard.writeText(text)
    notify({ group: 'info', text: 'Copied to clipboard' })
  } catch (e) { }
}

const setApiKey = async () => {
  apiKeyProcessing.value = true

  try {
    let res = await UserService.getUserApiKey()
    if (res && res.apiKey) {
      form.value.apiKey = res.apiKey
    }
  } catch (e) {
    notify({ group: 'error', text: `Unable to retrieve the API key.` })
  }
  apiKeyProcessing.value = false
}

// Member permissions can resolve after mount, so load once access is granted.
watch(canAccessDevelopers, (allowed) => {
  if (allowed) setApiKey()
}, { immediate: true })
</script>

<template>
  <div class="view">
    <Spinner v-if="apiKeyProcessing" text="Retrieving data..." classes="text-suraface-8 mt-6 lg:mt-24"
      spinner-color="var(--c-dark-8)" spinner-size="15px" />
    <div v-else>
      <div class="flex flex-col items-start gap-3 lg:w-1/3" v-if="canAccessDevelopers">
        <div v-if="apiKeyProcessing" class="input disabled">
          <i class="fa fa-spinner fa-spin"></i>
        </div>
        <FormInput label="API Key" v-else disabled size="md" v-model="form.apiKey" placeholder="no api key assigned"
          readonly />
        <div v-show="!apiKeyProcessing">
          <FormButton v-if="!form.apiKey" onclick="Intercom('show')" label="Request API Key" />
          <FormButton v-else type="main" @click="clipboardCopy(form.apiKey)" label="Copy" />
        </div>
      </div>
      <div class="text-center mt-6 lg:mt-12" v-else>
        <h4 class="text-surface-9 text-lg font-medium">You don’t have permission to access this.</h4>
        <p class="text-surface-6 text-base">Contact the team owner at <a class="text-surface-6 underline"
            :href="'mailto:' + userEmail">{{ userEmail }}</a> to gain access.</p>
      </div>
    </div>
  </div>
</template>

<style scoped></style>
